import React, { useState, useEffect } from "react";
import axios from "axios";
import { useChat } from "./Chat"; // Ensure this is correctly imported
import socket from "./socket";
import styles from "../../css/Messaging.css";


const useConversation = () => {
  const userId = localStorage.getItem("userId");
  const [selectedReceiverId, setSelectedReceiverId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const { messages, setMessages } = useChat(conversationId);

  useEffect(() => {
    socket.on("userTyping", ({ roomId, userName, typing }) => {
      if (roomId === conversationId) {
        setIsTyping(typing);
      }
    });

    return () => {
      socket.off("userTyping");
    };
  }, [conversationId]);

  const fetchPastMessages = async (roomId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://techservicemarket-backend.onrender.com/api/messages/${roomId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Past messages loaded:", response.data);
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching past messages:", error);
    }
  };

  const startConversation = async (receiverId, receiverName) => {
    setSelectedReceiverId(receiverId);
    setSelectedUserName(receiverName);
    const roomId = [userId, receiverId].sort().join("-");
    setConversationId(roomId);
    socket.emit("joinRoom", { conversationId: roomId });
    
    // Clear existing messages and fetch past messages
    setMessages([]);
    await fetchPastMessages(roomId);
  };

  return {
    selectedReceiverId,
    selectedUserName,
    conversationId,
    messages,
    setMessages,
    startConversation,
    isTyping,
  };
};

const Messaging = () => {
  const [users, setUsers] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [partners, setPartners] = useState([]);

  const userId = localStorage.getItem("userId");
  const [message, setMessage] = useState("");
  const [typingTimeout, setTypingTimeout] = useState(null);
  const {
    selectedReceiverId,
    selectedUserName,
    conversationId,
    messages,
    setMessages,
    startConversation,
    isTyping,
  } = useConversation();

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    console.log("Messages updated:", messages);
  }, [messages]);

  useEffect(() => {
    // Clean up any existing listeners first
    socket.off("receiveMessage");
    
    // Set up message listener
    socket.on("receiveMessage", (receivedMessage) => {
      console.log("Message received from server:", receivedMessage);
      setMessages((prev) => [...prev, receivedMessage]);
    });

    // Cleanup function
    return () => {
      socket.off("receiveMessage");
    };
  }, [setMessages]); // Only depend on setMessages

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const users = await axios.get(
        "https://techservicemarket-backend.onrender.com/api/users/",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUsers(users.data);

      const recruiters = await axios.get(
        "https://techservicemarket-backend.onrender.com/api/recruiter/",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setRecruiters(recruiters.data);

      const partners = await axios.get(
        "https://techservicemarket-backend.onrender.com/api/universitypartner/",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      setPartners(partners.data);


    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleTyping = (e) => {
    setMessage(e.target.value);

    if (conversationId) {
      if (typingTimeout) clearTimeout(typingTimeout);

      socket.emit("typing", {
        roomId: conversationId,
        userName: "You",
        typing: true,
      });

      const timeout = setTimeout(() => {
        socket.emit("typing", {
          roomId: conversationId,
          userName: "You",
          typing: false,
        });
      }, 1000);

      setTypingTimeout(timeout);
    }
  };

  const handleSendMessage = () => {
    if (!message.trim() || !conversationId) return;

    const newMessage = {
      conversationId,
      senderId: userId,
      message: message.trim(),
      timestamp: new Date().toISOString(),
    };

    // Emit message via socket
    console.log("Sending message:", newMessage);
    socket.emit("sendMessage", newMessage);

    // Clear input field
    setMessage("");

    socket.emit("typing", {
      roomId: conversationId,
      userName: "You",
      typing: false,
    });
  };

  return (
    <div className="messagingContainer">
      {/* User List */}
      <div className="userList">
        <h2>Users</h2>
        <table className="userTable">
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) =>
              user._id !== userId ? (
                <tr key={user._id}>
                  <td>
                    {user.firstName} {user.lastName}
                  </td>
                  <td>
                    {user.userRole}
                  </td>
                  <td>
                    <button
                    className="messageButton"
                      onClick={() =>
                        startConversation(
                          user._id,
                          `${user.firstName} ${user.lastName}`
                        )
                      }
                    >
                      Message
                    </button>
                  </td>
                </tr>
              ) : null
            )}
            {recruiters.map((recruiter) =>
              recruiter._id !== userId ? (
                <tr key={recruiter._id}>
                  <td>
                    {recruiter.companyName}
                  </td>
                  <td>
                    Recruiter
                  </td>
                  <td>
                    <button
                    className="messageButton"
                      onClick={() =>
                        startConversation(
                          recruiter._id,
                          recruiter.companyName
                        )
                      }
                    >
                      Message
                    </button>
                  </td>
                </tr>
              ) : null
            )}
            {partners.map((partner) =>
              partner._id !== userId ? (
                <tr key={partner._id}>
                  <td>
                    {partner.universityName}
                  </td>
                  <td>
                    University Partner
                  </td>
                  <td>
                    <button
                    className="messageButton"
                      onClick={() =>
                        startConversation(
                          partner._id,
                          partner.universityName
                        )
                      }
                    >
                      Message
                    </button>
                  </td>
                </tr>
              ) : null
            )}
          </tbody>
        </table>
      </div>

      {/* Chat Box */}
      <div className="chatBox">
        <h2>Chat Room: {selectedUserName || "Start messaging by selecting a user"}</h2>
        <div className="chatMessages">
          {messages?.map((msg, index) => (
            <div
              key={index}
              style={{
                textAlign: msg.senderId === userId ? "right" : "left",
                marginBottom: "10px",
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: msg.senderId === userId ? "#007bff" : "#e9ecef",
                  color: msg.senderId === userId ? "white" : "black",
                  padding: "8px 12px",
                  borderRadius: "15px",
                  maxWidth: "70%",
                  wordWrap: "break-word",
                }}
              >
                {msg.message}
              </div>
            </div>
          ))}
        </div>

        {isTyping && (
          <div style={{ color: "#666", fontSize: "0.9em", marginBottom: "5px" }}>
            {selectedUserName} is typing...
          </div>
        )}

        <div style={{ display: "flex", gap: "10px" }}>
          <input
            type="text"
            value={message}
            onChange={handleTyping}
            onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            placeholder="Type a message..."
            style={{ flex: 1, padding: "8px" }}
          />
          <button
            className="sendButton"
            onClick={handleSendMessage}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default Messaging;
